import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useClub } from '../App';
import { toast } from 'sonner';
import api from '../utils/api';
import { Trophy, Plus, X, Calendar, Users, Swords, ArrowRight, UserPlus2, Shuffle } from 'lucide-react';

const GAME_TYPES = [
  { value: 'singles', label: 'Singles (1v1)', description: 'Individual matches' },
  { value: 'doubles_changing', label: 'Doubles - Changing Partners', description: 'Partners rotate each game for variety' },
  { value: 'doubles_fixed_assigned', label: 'Doubles - Fixed Partners (Assigned)', description: 'Admin assigns permanent partner pairs' },
  { value: 'doubles_fixed_random', label: 'Doubles - Fixed Partners (Random)', description: 'System randomly assigns permanent partners' },
];

const RATING_MODES = [
  { value: 'lifetime', label: 'Lifetime Ratings', description: 'Use player lifetime ratings for pairing' },
  { value: 'club', label: 'Club Ratings', description: 'Use club-level ratings for pairing' },
  { value: 'tournament_isolated', label: 'Tournament Only', description: 'All start at 1500, isolated ratings' },
];

function CreateTournamentModal({ groupId, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '',
    description: '',
    start_date: '',
    num_courts: 2,
    game_type: 'doubles_changing',
    rating_mode: 'lifetime',
    is_fun: false,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.start_date) { toast.error('Name and start date are required'); return; }
    setLoading(true);
    try {
      await api.post('/tournaments', { ...form, group_id: groupId });
      toast.success(`Tournament "${form.name}" created!`);
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create tournament');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-slate-900/40 overflow-y-auto">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full max-w-lg p-5 my-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Create Tournament</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1" data-testid="close-tournament-modal-btn"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3" data-testid="create-tournament-form">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Tournament Name</label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Club Championship 2025"
              className="w-full h-9 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              data-testid="tournament-name-input"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Optional description"
              rows={2}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
              data-testid="tournament-description-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Start Date</label>
              <input
                type="date"
                value={form.start_date}
                onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                className="w-full h-9 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                data-testid="tournament-start-date-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Courts</label>
              <select
                value={form.num_courts}
                onChange={e => setForm(f => ({ ...f, num_courts: parseInt(e.target.value) }))}
                className="w-full h-9 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                data-testid="tournament-num-courts-select"
              >
                {[1,2,3,4,5,6,8,10].map(n => <option key={n} value={n}>{n} court{n > 1 ? 's' : ''}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">Game Type</label>
            <div className="grid grid-cols-1 gap-1.5">
              {GAME_TYPES.map(gt => (
                <label
                  key={gt.value}
                  className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-all ${
                    form.game_type === gt.value ? 'border-green-500 bg-green-50' : 'border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <input
                    type="radio" name="game_type" value={gt.value}
                    checked={form.game_type === gt.value}
                    onChange={() => setForm(f => ({ ...f, game_type: gt.value }))}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-slate-800">{gt.label}</p>
                    <p className="text-[10px] text-slate-500">{gt.description}</p>
                  </div>
                  {gt.value === 'singles' && <UserPlus2 size={14} className="text-slate-400" />}
                  {gt.value === 'doubles_changing' && <Shuffle size={14} className="text-slate-400" />}
                </label>
              ))}
            </div>
          </div>

          <div className={`flex items-center gap-2.5 p-2.5 rounded-lg border transition-all ${form.is_fun ? 'bg-amber-50 border-amber-300' : 'bg-slate-50 border-slate-200'}`}>
            <input
              type="checkbox" id="is_fun"
              checked={form.is_fun}
              onChange={e => setForm(f => ({ ...f, is_fun: e.target.checked }))}
              className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
              data-testid="tournament-is-fun-checkbox"
            />
            <div>
              <label htmlFor="is_fun" className="text-xs font-medium text-slate-700 cursor-pointer">Fun Tournament (no rating impact)</label>
              <p className="text-[10px] text-slate-500">Scores tracked but player ratings unaffected</p>
            </div>
          </div>

          {!form.is_fun && (
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">Rating Type for Pairing</label>
              <div className="grid grid-cols-1 gap-1.5">
                {RATING_MODES.map(rm => (
                  <label
                    key={rm.value}
                    className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-all ${
                      form.rating_mode === rm.value ? 'border-green-500 bg-green-50' : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="radio" name="rating_mode" value={rm.value}
                      checked={form.rating_mode === rm.value}
                      onChange={() => setForm(f => ({ ...f, rating_mode: rm.value }))}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <p className="text-xs font-medium text-slate-800">{rm.label}</p>
                      <p className="text-[10px] text-slate-500">{rm.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 h-9 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium rounded-lg transition-all" data-testid="cancel-create-tournament-btn">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="flex-1 h-9 bg-green-600 hover:bg-green-700 disabled:bg-slate-300 text-white text-xs font-semibold rounded-lg transition-all" data-testid="submit-create-tournament-btn">
              {loading ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function TournamentsPage() {
  const { user } = useAuth();
  const { selectedClub } = useClub();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [closing, setClosing] = useState(null);

  const canManage = selectedClub?.my_role === 'admin' || selectedClub?.my_role === 'contributor';

  const fetchTournaments = async () => {
    if (!selectedClub) return;
    try {
      const res = await api.get(`/tournaments?group_id=${selectedClub.id}`);
      setTournaments(res.data);
    } catch {
      toast.error('Failed to load tournaments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTournaments(); }, [selectedClub]);

  const handleClose = async (e, t) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Close tournament "${t.name}"? All open games will be cancelled.`)) return;
    setClosing(t.id);
    try {
      await api.post(`/tournaments/${t.id}/close`);
      toast.success(`Tournament "${t.name}" closed`);
      fetchTournaments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to close tournament');
    } finally {
      setClosing(null);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Tournaments</h1>
          <p className="text-[11px] text-slate-500">{tournaments.length} tournament{tournaments.length !== 1 ? 's' : ''}</p>
        </div>
        {canManage && (
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-lg transition-all active:scale-95"
            data-testid="open-create-tournament-btn"
          >
            <Plus size={13} /> New
          </button>
        )}
      </div>

      {loading ? (
        <div className="space-y-2">
          {[1,2,3].map(i => <div key={i} className="bg-white rounded-xl border border-slate-200 h-16 animate-pulse" />)}
        </div>
      ) : tournaments.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-10 text-center">
          <Trophy className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No tournaments yet</p>
          {canManage && (
            <button
              onClick={() => setShowModal(true)}
              className="mt-3 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-lg transition-all"
              data-testid="empty-create-tournament-btn"
            >
              Create First Tournament
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2" data-testid="tournaments-grid">
          {tournaments.map(t => (
            <Link
              key={t.id}
              to={`/tournaments/${t.id}`}
              className="bg-white rounded-xl border border-slate-200 shadow-sm px-3 py-2.5 hover:shadow-md transition-all duration-200 group flex items-center gap-2.5"
              data-testid={`tournament-card-${t.id}`}
            >
              <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                <Trophy size={16} className="text-green-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <h3 className="font-bold text-slate-900 text-sm group-hover:text-green-700 transition-colors truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{t.name}</h3>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${
                    t.status === 'active'
                      ? 'bg-green-50 text-green-700 border border-green-200'
                      : 'bg-slate-100 text-slate-600 border border-slate-200'
                  }`}>{t.status}</span>
                  {t.is_fun && <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium bg-amber-50 text-amber-700 border border-amber-200 flex-shrink-0">Fun</span>}
                </div>
                <div className="flex items-center gap-2 text-[10px] text-slate-500 flex-wrap">
                  <span className="flex items-center gap-0.5"><Calendar size={10} />{t.start_date}</span>
                  <span className="flex items-center gap-0.5"><Users size={10} />{t.player_count}</span>
                  <span className="flex items-center gap-0.5"><Swords size={10} />{t.game_count}</span>
                  <span>{t.num_courts}c</span>
                  <span className={`px-1 py-0.5 rounded text-[9px] font-medium ${
                    t.game_type === 'singles' ? 'bg-blue-50 text-blue-600' :
                    t.game_type === 'doubles_changing' ? 'bg-purple-50 text-purple-600' :
                    'bg-amber-50 text-amber-600'
                  }`}>
                    {t.game_type === 'singles' ? '1v1' : t.game_type === 'doubles_changing' ? '2v2 Rot' : '2v2 Fix'}
                  </span>
                </div>
              </div>
              {canManage && t.status === 'active' && (
                <button
                  onClick={(e) => handleClose(e, t)}
                  disabled={closing === t.id}
                  className="px-2 py-1 text-[10px] font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-all flex-shrink-0 disabled:opacity-50"
                  data-testid={`close-tournament-btn-${t.id}`}
                >
                  {closing === t.id ? '…' : 'Close'}
                </button>
              )}
              <ArrowRight size={14} className="text-slate-300 group-hover:text-green-500 transition-colors flex-shrink-0" />
            </Link>
          ))}
        </div>
      )}

      {showModal && (
        <CreateTournamentModal groupId={selectedClub?.id} onClose={() => setShowModal(false)} onCreated={fetchTournaments} />
      )}
    </div>
  );
}
